import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  SetStateAction,
  Dispatch,
} from "react";
import { Endpoint } from "../api/base";
import {
  pastelColors,
  truncateString,
} from "../components/shared/helpers/helper";
import axios from "axios";

import { Snackbar, makeStyles } from "@material-ui/core";
import { Typography } from "@mui/material";
import { CircularProgressWithLabel } from "../components/shared/CircularProgressWithLabel";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import moment from "moment";
import { useToast } from "./ToastContext";

import { CaseDataResponse, FileItem } from "../types/CaseTypes";
import { FieldResource } from "../types/FieldResourceType";
import { tokens } from "../theme/tokens";

const useStyles = makeStyles((theme) => ({
  alert: {
    padding: "0px",
    margin: "0px",
    "& .MuiAlert-message": {
      padding: "0px",
      width: "100%",
    },
  },

  uploadItemAlert: {
    borderRadius: "8px",
    boxShadow:
      "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "& .MuiAlert-icon": {
      padding: "8px 0",
      marginRight: "8px",
    },
    "& .MuiAlert-message": {
      padding: "8px 0",
      width: "100%",
    },
  },
  snackbarCustom: {
    maxWidth: "400px",
    width: "100%",
  },
  uploadContainer: {
    maxHeight: "32vh",
    overflowY: "auto",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    // Hide scrollbar for Chrome/Safari
    "&::-webkit-scrollbar": { width: "4px" },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0,0,0,0.1)",
      borderRadius: "10px",
    },
  },
  uploadItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(10px)",
    borderRadius: "12px",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
  },
  fileName: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#2d3748",
    marginRight: "12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statusText: {
    fontSize: "0.8rem !important",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.025em",
  },
}));

const initialValues: CaseDataResponse = {
  case_detail: {
    status: "",
    primary_subject: "",
    flag: "",
    client_id: "",
    user_id: "",
    file_no: "",
    case_details: {
      client_information: {
        client_file_no: "",
        client_loan: "",
        phone_number: "",
        date_of_sale: "",
        reopened_date: "",
      },
      budget_information: {
        scheduled: 0,
        used: 0,
      },
      case_information: {
        file_no: "",
        date_referred: "",
        follow_up: "",
        due_date: "",
        case_type: "",
        subject_first_name: "",
        subject_last_name: "",
        subject_property_address: {
          street_address: "",
          city: "",
          state: "",
          zip_code: "",
        },
        sale_date: "",
        city: "",
        death_certificate_ordered: "",
        death_certificate_received: "",
        referral_notes: "",
        case_info: "",
      },
      field_resource_information: [
        {
          id: "",
          date_hired: "",
          due_date: "",
          hours_authorized: 0,
          hours_billed: 0,
          miles_billed: 0,
          database_billed: 0,
          report_received_date: "",
          invoice_received_date: "",
          invoice_approved_date: "",
          rating: 0,
          type: "",
        },
      ],
      assigment_information: {
        investigator_name: "",
        event: "",
        supervisor_name: "",
      },
      Subjects_information: [],
      files: [],
    },
  },
  case_search_results: {
    file_no: "",
    searchResults: {},
  },
};

interface UploadFileProgress {
  id: string;
  name: string;
  size: number;
  progress: number;
  uploaded: boolean;
  error: boolean;
  completedAt?: number;
}

interface UploadLoadingState {
  uploads: UploadFileProgress[];
  isActive: boolean;
}

interface StoreContextValue {
  users: string[];
  clients: string[] | null;
  fieldResources: FieldResource[] | null;
  setFieldResources: Dispatch<SetStateAction<FieldResource[] | null>>;
  avatarColor: string;
  otherAvatarColor: string;
  uploadLoading: UploadLoadingState;
  setUploadLoading: (state: UploadLoadingState) => void;
  addUpload: (file: File) => string;
  updateUploadProgress: (id: string, progress: number) => void;
  markUploadComplete: (id: string, success: boolean) => void;
  removeUpload: (id: string) => void;

  UploadFilesContext: (
    files: File[],
    file_no: string,
  ) => Promise<string[] | undefined>;
  handleSetFile: (data: string, type: string) => void;
  files: FileItem[];
  setFiles: Dispatch<SetStateAction<FileItem[]>>;
  handleSubmitFile: (value: CaseDataResponse) => void;
  data: CaseDataResponse;
  setData: (data: CaseDataResponse) => void;
}

export const StoreContext = createContext<StoreContextValue | undefined>(
  undefined,
);

export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreWrapper");
  }
  return context;
};

interface StoreWrapperProps {
  children: ReactNode;
}

export const StoreWrapper = (props: StoreWrapperProps) => {
  const classes = useStyles();
  const { showToast } = useToast();
  const [users, setUsers] = useState<string[]>([]);
  const [clients, setClients] = useState<string[] | null>(null);
  const [fieldResources, setFieldResources] = useState(null);
  const [avatarColor, setAvatarColor] = useState<string>("");
  const [otherAvatarColor, setOtherAvatarColor] = useState<string>("");

  const [data, setData] = useState<CaseDataResponse>(initialValues);

  const [uploadLoading, setUploadLoading] = useState<UploadLoadingState>({
    uploads: [],
    isActive: false,
  });
  const [files, setFiles] = useState<FileItem[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setAvatarColor(
        pastelColors[Math.floor(Math.random() * pastelColors.length)],
      );
      setOtherAvatarColor(
        pastelColors[Math.floor(Math.random() * pastelColors.length)],
      );

      try {
        const res = await Endpoint.getClientsAndUsers();
        setClients(res.data.clients);
        setUsers(res.data.users);
      } catch (err) {
        console.log(err);
      }

      try {
        const res = await Endpoint.getFieldResources();
        setFieldResources(res.data);
      } catch (err) {
        console.log(err);
      }
    };

    fetchData();
  }, []);

  // Auto-cleanup completed uploads after 2 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setUploadLoading((prev) => {
        if (prev.uploads.length === 0) return prev; // Don't trigger state update if empty
        const now = Date.now();
        const filtered = prev.uploads.filter((upload) => {
          // If it hasn't finished (no completedAt), keep it.
          if (!upload.completedAt) return true;

          // If finished, keep it for 3 seconds, then remove.
          return now - upload.completedAt < 3000;
        });

        return {
          uploads: filtered,
          isActive: filtered.length > 0,
        };
      });
    }, 500);

    return () => clearInterval(timer);
  }, []);

  const addUpload = (file: File): string => {
    const timestamp = Date.now();
    const id =
      timestamp.toString() + Math.random().toString(36).substring(2, 5);
    const uploadData = {
      id,
      name: file?.name || "Unknown",
      size: file?.size || 0,
      progress: 0,
      uploaded: false,
      error: false,
    };

    setUploadLoading((prev) => ({
      uploads: [...prev.uploads, uploadData],
      isActive: true,
    }));

    return id;
  };

  const updateUploadProgress = (id: string, progress: number) => {
    setUploadLoading((prev) => ({
      ...prev,
      uploads: prev.uploads.map((upload) =>
        upload.id === id ? { ...upload, progress } : upload,
      ),
    }));
  };

  const markUploadComplete = (id: string, success: boolean) => {
    setUploadLoading((prev) => ({
      ...prev,
      uploads: prev.uploads.map((upload) =>
        upload.id === id
          ? {
              ...upload,
              uploaded: success,
              error: !success,
              progress: 100,
              completedAt: Date.now(),
            }
          : upload,
      ),
    }));
  };

  const removeUpload = (id: string) => {
    setUploadLoading((prev) => ({
      uploads: prev.uploads.filter((upload) => upload.id !== id),
      isActive: prev.uploads.filter((upload) => upload.id !== id).length > 0,
    }));
  };

  const handleSubmitFile = (value: CaseDataResponse) => {
    let sendObj = value;
    try {
      sendObj.case_detail.case_details.files = files;

      if (files.length > 0) {
        sendObj.case_detail.case_details.case_information.file_due = false;
      }

      Endpoint.saveCase(JSON.stringify(sendObj))
        .then(async (res) => {
          showToast("Data updated successfully", "success");
        })
        .catch((err) => {
          console.log(err);
          showToast("Something went wrong, please try again later", "error");
        });
    } catch (err) {
      console.log(err);
      showToast("Something went wrong, please try again later", "error");
    }
  };

  // New batch upload function
  const UploadFilesContext = async (
    filesToUpload: File[],
    file_no: string,
  ): Promise<string[] | undefined> => {
    try {
      // Step 1: Get all signed URLs in one API call
      const fileNames = filesToUpload.map((file) => file.name);
      const response = await Endpoint.uploadMultiFile({
        file_no,
        files: fileNames,
      });

      // Expected response format:
      // {
      //   file_no: "2025TEST50015",
      //   signed_urls: [
      //     {
      //       original_name: "a.pdf",
      //       key: "uploads/2025TEST50015/a.pdf",
      //       signed_url: { url: string, fields: { ... } }
      //     },
      //     ...
      //   ]
      // }
      const signedUrls = response.data.signed_urls;
      if (!signedUrls || signedUrls.length !== filesToUpload.length) {
        throw new Error("Mismatch between files and signed URLs");
      }

      // Step 2: Upload each file to its respective signed URL
      const uploadPromises = filesToUpload.map(async (file, index) => {
        const uploadId = addUpload(file);
        const uploadData = signedUrls[index];
        const { url, fields } = uploadData.signed_url;

        try {
          const formData = new FormData();

          // Append all fields from the signed URL response
          Object.keys(fields).forEach((key) => {
            formData.append(key, fields[key]);
          });

          // Append the actual file
          formData.append("file", file);

          const config = {
            onUploadProgress: (progressEvent: ProgressEvent) => {
              const percentCompleted = Math.round(
                ((progressEvent.loaded ?? 0) * 100) /
                  (progressEvent.total ?? 1),
              );
              updateUploadProgress(uploadId, percentCompleted);
            },
            headers: {
              "Content-Type": "multipart/form-data",
            },
          };

          await axios.post(url, formData, config);
          markUploadComplete(uploadId, true);

          return uploadData.key; // Return the S3 key
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          markUploadComplete(uploadId, false);
          throw error;
        }
      });

      const uploadedKeys = await Promise.all(uploadPromises);
      return uploadedKeys;
    } catch (error) {
      console.error("Batch upload error:", error);
      return undefined;
    }
  };

  const handleSetFile = (data: string, type: string) => {
    let x = data.split("/");
    setFiles([
      ...files,
      {
        key: data,
        name: x[1],
        dateCreated: moment().format("LLLL"),
        type: type,
      },
    ]);
  };

  return (
    <StoreContext.Provider
      value={{
        users,
        clients,
        fieldResources,
        setFieldResources,
        avatarColor,
        otherAvatarColor,
        uploadLoading,
        setUploadLoading,
        addUpload,
        updateUploadProgress,
        markUploadComplete,
        removeUpload,
        UploadFilesContext,
        handleSetFile,
        files,
        setFiles,
        handleSubmitFile,
        data,
        setData,
      }}
    >
      {props.children}

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={uploadLoading.isActive}
        className={classes.snackbarCustom}
      >
        <div className={classes.uploadContainer}>
          {uploadLoading.uploads.map((upload) => {
            // Determine State
            const isDone = upload.uploaded;
            const isError = upload.error;
            const isLoading = !isDone && !isError;

            return (
              <div key={upload.id} className={classes.uploadItem}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography className={classes.fileName}>
                    {upload.name ? truncateString(upload.name) : "File Upload"}
                  </Typography>

                  {/* Status Subtext */}
                  <Typography
                    className={classes.statusText}
                    style={{
                      color: isDone
                        ? tokens.color.status.success
                        : isError
                          ? tokens.color.status.error
                          : tokens.color.brand.primary,
                    }}
                  >
                    {isDone
                      ? "Completed"
                      : isError
                        ? "Failed"
                        : `${upload.progress}% Uploading`}
                  </Typography>
                </div>

                <div
                  style={{
                    marginLeft: "16px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {isDone && (
                    <DoneIcon
                      style={{ color: tokens.color.status.success, fontSize: 28 }}
                    />
                  )}
                  {isError && (
                    <ErrorIcon
                      style={{ color: tokens.color.status.error, fontSize: 28 }}
                    />
                  )}
                  {isLoading && (
                    <CircularProgressWithLabel
                      value={upload.progress}
                      size={32}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Snackbar>
    </StoreContext.Provider>
  );
};
